/**
 * Supressao de ruido do microfone por rede neural (GTCRN), rodando inteiro
 * no navegador via WebAssembly — mesma familia de tecnica que o Krisp do
 * Discord usa, sem depender de nenhum servico externo. `@sapphi-red/web-
 * noise-suppressor` empacota o modelo e o worklet prontos; aqui so montamos
 * o grafo: microfone cru -> no do worklet -> destino, que vira a trilha
 * publicada de verdade.
 */
import { GtcrnWorkletNode, loadGtcrn } from "@sapphi-red/web-noise-suppressor";
import { manterContextoVivo } from "@/media/audioContextGuard";
import gtcrnWasmUrl from "@sapphi-red/web-noise-suppressor/gtcrn.wasm?url";
import gtcrnWorkletUrl from "@sapphi-red/web-noise-suppressor/gtcrnWorklet.js?url";

export type ProcessedMic = {
  stream: MediaStream;
  dispose: () => void;
};

/** Buscado uma unica vez; o binario compilado e o mesmo para qualquer AudioContext. */
let wasmBinaryPromise: Promise<ArrayBuffer> | null = null;
function loadWasmBinary(): Promise<ArrayBuffer> {
  if (!wasmBinaryPromise) wasmBinaryPromise = loadGtcrn({ url: gtcrnWasmUrl });
  return wasmBinaryPromise;
}

/**
 * `null` quando o navegador nao suporta AudioWorklet ou o WASM falha ao
 * carregar — quem chama deve cair de volta para a trilha crua do microfone
 * em vez de travar a captura.
 */
export async function applyNoiseSuppression(
  rawStream: MediaStream,
  aoPerderOContexto?: () => void,
): Promise<ProcessedMic | null> {
  if (typeof AudioContext === "undefined") return null;
  let context: AudioContext;
  try {
    context = new AudioContext({ sampleRate: 48_000, latencyHint: "interactive" });
  } catch {
    return null;
  }
  if (!context.audioWorklet) {
    void context.close().catch(() => undefined);
    return null;
  }
  try {
    const [wasmBinary] = await Promise.all([
      loadWasmBinary(),
      context.audioWorklet.addModule(gtcrnWorkletUrl),
    ]);
    const source = context.createMediaStreamSource(rawStream);
    const node = new GtcrnWorkletNode(context, { maxChannels: 1, wasmBinary });
    const destination = context.createMediaStreamDestination();
    source.connect(node).connect(destination);
    const pararVigia = manterContextoVivo(context, { aoDesistir: aoPerderOContexto });
    return {
      stream: destination.stream,
      dispose: () => {
        pararVigia();
        node.destroy();
        try {
          source.disconnect();
        } catch {
          // contexto pode ja ter fechado
        }
        void context.close().catch(() => undefined);
      },
    };
  } catch {
    void context.close().catch(() => undefined);
    return null;
  }
}
